import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { CheckCircle, Clock, Phone, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const TasksPage = () => {
  const { api } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    try {
      const response = await api.get('/reminders?status=pending');
      setReminders(response.data || []);
    } catch (err) {
      toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkDone = async (reminderId) => {
    try {
      await api.put(`/reminders/${reminderId}?status=done`);
      toast.success('Reminder marked as done');
      fetchReminders();
    } catch (err) {
      toast.error('Failed to update reminder');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="tasks-loading">
        <Skeleton className="h-8 w-48" />
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
          Follow-ups & Tasks
        </h1>
        <p className="text-muted-foreground">
          Pending reminders that need your attention
        </p>
      </div>

      {reminders.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
            <h3 className="text-lg font-medium">All caught up!</h3>
            <p className="text-muted-foreground">No pending follow-ups at the moment.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reminders.map((reminder) => (
            <Card key={reminder.id} className="card-hover" data-testid={`reminder-${reminder.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold">{reminder.leadName}</h3>
                      <Badge variant="outline" className="text-orange-600 border-orange-300">
                        <Clock className="mr-1 h-3 w-3" />
                        Overdue
                      </Badge>
                    </div>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Phone className="h-3 w-3" />
                        {reminder.leadMobile}
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3 w-3" />
                        Due: {new Date(reminder.reminderDate).toLocaleString()}
                      </div>
                      {reminder.note && (
                        <p className="mt-2 text-foreground">{reminder.note}</p>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={() => handleMarkDone(reminder.id)}
                    data-testid={`mark-done-${reminder.id}`}
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Mark Done
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TasksPage;
